import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          // 700: "#3d3d3d",
          700: "#a3a3a3" , // manually changed (grey)
          800: "#292929",
          900: "#141414",
          950: "#434C5F", // manually changed (default chip color)
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          450: "#d0d1d5", // manually changed (grey - white)
          500: "#141b2d",
          // 600: "#101624",
          600: "#1F2A40", // manually changed (navy)
          // 700: "#0c101b",
          700: "#d0d1d5", // manually changed (grey - white)
          750: "#1F2A40", // manually changed (navy)
          // 800: "#080b12",
          800: "#e0e0e0", // manually changed (grey - white)
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
        purpleAccent: {
            100: "#F9B3E0",
            200: "#F194DE",
            300: "#E374DB",
            400: "#D354D9",
            500: "#BC34D7",
            600: "#A41DD4",
            700: "#8C00D2",
            800: "#6A00A8",
            900: "#4A007E",  
        },
        yellowAccent: {
            100: "#FEFACC",
            200: "#FDED99",
            300: "#FBDD66",
            400: "#FACC33",
            500: "#FABA00",
            600: "#DAA400",
            700: "#BA8C00",
            800: "#9A7500",
            900: "#7A5D00",
        },
        orangeAccent: {
            100: "#FFE0B2",
            200: "#FFCC80",
            300: "#FFB74D",
            400: "#FFA726",
            500: "#FF8C00",
            600: "#E07B00",
            700: "#C06A00",
            800: "#A05800",
            900: "#804700",
        },
      }
    : {
        grey: {
          100: "#FEFEFE", // manually changed (white)
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
          950: "#EBEAEB", // // manually changed (default chip color)
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#172233", // manually changed (navy)
          450: "#172233", // manually changed (navy)
          500: "#141b2d",
          600: "#FFFFFF", // manually changed (white)
          700: "#F5F5F7", // manually changed (white)
          750: "#F5F5F7", // manually changed (white)
          800: "#e0e0e0",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#CFFD5E", // manually changed (green)
        //   500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#599DD5", // manually changed (blue)
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        purpleAccent: {
            100: "#4A007E",
            200: "#6A00A8",
            300: "#8C00D2",
            400: "#A41DD4",
            500: "#BC34D7",
            600: "#D354D9",
            700: "#E374DB",
            800: "#F194DE",
            900: "#F9B3E0",
        },
        yellowAccent: {
            100: "#7A5D00",
            200: "#9A7500",
            300: "#BA8C00",
            400: "#DAA400",
            500: "#FABA00",
            600: "#FACC33",
            700: "#FBDD66",
            800: "#FDED99",
            900: "#FEFACC",
        },
        orangeAccent: {
            100: "#804700",
            200: "#A05800",
            300: "#C06A00",
            400: "#E07B00",
            500: "#FF8C00",
            600: "#FFA726",
            700: "#FFB74D",
            800: "#FFCC80",
            900: "#FFE0B2",
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[450],
              light: colors.primary[600],
              dark: colors.primary[750],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
            //   light: colors.primary[600],
            //   dark: colors.primary[750],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#f5f5f7",
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 14,
      },
      h7: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 12,
      },
      h8: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 10,
      },
      h9: {
        fontFamily: ["Source Sans", "sans-serif"].join(","),
        fontSize: 8,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState(localStorage.getItem("mode") || "light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => (prev === "light" ? "dark" : "light"));
        const mode = localStorage.getItem("mode") || "light";
        localStorage.setItem("mode", mode === "light" ? "dark" : "light");
      },
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};